import { Link } from "@/components/Link";
import { SingleColumn } from "@/layouts/single-column";

import { CONTACT_EMAIL } from "../../utils";

export default function LegalTerms() {
  return (
    <SingleColumn.Root>
      <SingleColumn.Main>
        <div className="card bg-base-100 p-6">
          <h2 className="text-3xl font-bold mb-8">Terms and Conditions</h2>

          <article className="flex flex-col gap-1 text-justify">
            <p>
              <strong>Effective May 7, 2024</strong>
            </p>
            <p>
              <strong>
                DreamVault is a membership platform that helps connect Builders with Dreamers they love. Our mission is
                to put Dreamers first, and these terms attempt to do that. We know that most people skim through terms
                of use because they&apos;re boring, but we have done everything we can to make this easy to get through.
                Above every section, we&apos;ll summarize the most important parts, but these summaries are not legally
                binding, so please look at the full version of the text for a complete understanding of these terms.
              </strong>
            </p>
            <p>
              <strong>
                PLEASE READ THESE TERMS CAREFULLY, AS THEY GOVERN YOUR USE OF THE DREAMVAULT PLATFORM, WEBSITES, APPS,
                AND SERVICES. PARTICULARLY SECTION 10 (BINDING ARBITRATION; CLASS ACTION/JURY TRIAL WAIVER), WHICH
                AFFECTS YOUR RIGHTS IN THE EVENT OF A DISPUTE BETWEEN US.
              </strong>
            </p>
            <p>
              <strong>Welcome to DreamVault!</strong>
            </p>
            <p>
              <em>To summarize: by using DreamVault you agree to these terms.</em>
            </p>
            <p>
              These are DreamVault&apos;s Terms of Use, and they apply to all users of the DreamVault platform. “We,”
              “our,” and “us” refer to Dreamvault LLC “DreamVault” refers to this platform and the services offered by
              us, including the DreamVault iOS app, the DreamVault Android app, DreamVault APIs, and{" "}
              <a target="_blank" href="http://www.dreamvault.co">
                <span className="text-primary">www.dreamvault.co</span>
              </a>{" "}
              and related websites. By using DreamVault, you agree to these terms and to the other policies we post,
              including the{" "}
              <Link className="text-primary underline" to="/legal/privacy">
                Privacy Policy
              </Link>
              ,{" "}
              <Link className="text-primary underline" to="/legal/guidelines/community">
                Community Guidelines
              </Link>
              , and{" "}
              <Link className="text-primary underline" to="/legal/guidelines/benefit">
                Benefit Guidelines
              </Link>
              . Please read them carefully and let us know if you have any questions. For information about our data
              practices, please see our{" "}
              <Link className="text-primary underline" to="/legal/privacy">
                Privacy Policy
              </Link>{" "}
              and our{" "}
              <Link className="text-primary underline" to="/legal/policy/cookie">
                Cookie Policy
              </Link>
              . We collect, use, and share information in accordance with those policies.
            </p>
            <p>
              <strong>Your account</strong>
            </p>
            <p>
              <em>
                To summarize: You must be at least 13 years old to use DreamVault. You are responsible for your account.
              </em>
            </p>
            <p>
              When you create an account, you must provide us with accurate information, in good faith, and you agree to
              keep your information updated if it changes. To create an account or otherwise use DreamVault, you must be
              at least 13 years old and you must also be old enough to consent to the processing of your personal data
              in your country (in some countries we may allow your parent or guardian to do so on your behalf). You must
              be at least 18 years old or have your parent&apos;s or legal guardian&apos;s permission to have a Dreamer
              page on DreamVault or to purchase an offering on DreamVault. You are responsible for anything that occurs
              when anyone is signed in to your account, as well as the security of the account. Please contact us at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>{" "}
              immediately if you believe your account is compromised. You can learn more about security in our Security
              section below.
            </p>
            <p>
              <strong>Abusive conduct</strong>
            </p>
            <p>
              <em>To summarize: Be responsible and don&apos;t violate our terms or policies.</em>
            </p>
            <p>
              You are responsible for all activity on your account. If you violate our terms or policies, then we may
              terminate your account. Don&apos;t do anything illegal, abusive towards others, that abuses DreamVault in
              a technical way, or that exploits DreamVault in an unintended manner that is detrimental to us, like using
              DreamVault as a storage platform. If you are a Dreamer earning money on DreamVault, we may be exposed to
              risk based on what you do with those funds. As a result, we may also look at what you do outside of
              DreamVault. You can find more detailed information in the{" "}
              <Link className="text-primary underline" to="/legal/guidelines/community">
                Community Guidelines
              </Link>{" "}
              and security policies herein. These terms and our policies cover most issues, but we reserve the right to
              add new policies in order to take action to prevent new issues that may arise from time to time.
            </p>
            <p>
              <strong>Dreamers</strong>
            </p>
            <p>
              <em>
                To summarize: A Dreamer is someone who creates a page on DreamVault to engage with Builders who purchase
                offerings on DreamVault to support the Dreamer&apos;s creations. Dreamers can create wish lists of items
                for Builders to purchase as gifts for Dreamers, or curate a storefront, which is a list of items that
                Builders can purchase for themselves from our merchandise marketplace. There are a lot of details below
                involving payments, fees, taxes, and restrictions that you should read in full if you are a Dreamer.
              </em>
            </p>
            <p>
              <strong>Dreamer Page</strong>
            </p>
            <p>
              To become a Dreamer, simply launch your page to start allowing others to follow you. When you join
              DreamVault, you become part of the DreamVault community. You can use Dreamer tools that we provide to,
              among other things, showcase your creations, engage with the DreamVault community, and provide merchandise
              to Builders through our extensive merchandise marketplace. On DreamVault, you can provide your Builders
              something exciting that gives them special benefits they want, including additional access, merchandise,
              exclusive creations, and engaging community experiences. In turn, you will receive loyal support from your
              Builders, and revenue from offerings that Builders have purchased on DreamVault.
            </p>
            <p>
              <strong>Payments</strong>
            </p>
            <p>
              As a Dreamer, you may make offerings available on DreamVault on a one-time basis. Unless otherwise
              indicated, all prices, fees and other charges are in U.S. dollars, and all payments shall be in U.S.
              currency. Also, where supported and subject to your controls as applicable, we may automatically increase
              the prices of offerings to Builders to account for the fees imposed by the affiliate platforms on which
              the associated purchases were made in a manner that may be determined at our sole discretion. Our list of
              current affiliate platforms we work with include, but are not limited to: Shoppable, Impact, Narrative,
              Amazon, and Skimlinks. Payments from the affiliate platforms generally range from 3% to 20%.
            </p>
            <p>
              We typically handle payments issues such as fraud, chargebacks, and resolution of payments disputes,
              though, for purchases made through certain platforms, the associated platforms may handle these payments
              issues. We try to provide timely access to the funds you&apos;ve earned on DreamVault. Purchases made on
              affiliate platforms and different levels and types of DreamVault service may have different timelines
              associated with funds becoming accessible to you and/or may require that reserves of funds are withheld
              from becoming accessible to you. You may, however, occasionally experience further delays in accessing
              these funds.
            </p>
            <p>
              We may also block or withhold access to these funds for violations of our terms or policies or for
              compliance reasons, including collecting tax reporting information. When this happens, we try to
              communicate the reason to you promptly. If you have questions about access to funds being blocked or
              withheld, please email us at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              . In order to protect Dreamers, we may block Builders&apos; payments if we believe them to be fraudulent.
              Sometimes activities like refunds to Builders and chargebacks from Builders can put your account balance
              into the negative and any fees you&apos;ve incurred from DreamVault or the affiliate platforms on which
              the purchases were made, as described below, may not be refunded to you when such refunds and chargebacks
              are processed. If your balance becomes negative, we reserve the right to recover those funds from future
              payments.
            </p>
            <p>
              <strong>Fees</strong>
            </p>
            <p>
              As a Dreamer, there are fees associated with your offerings on DreamVault. The general nature of these
              fees is summarized below. These fees may increase with notice to you:
            </p>
            <ul>
              <li>
                The platform fee, is currently calculated as ten percent (10%) of successfully processed payments by the
                affiliate platforms. For service levels and types that include merch, please note that, in addition to
                the platform fee for using the merchandise marketplace, we also charge you for the cost of the
                merchandise itself based on the quantity and SKUs of the associated items.
              </li>
            </ul>
            <p>
              You can see the fees for any offerings on the DreamVault platform. Also, as described above, when
              offerings are purchased on affiliate platforms, those platforms may impose fees for those purchases. We
              endeavor to provide you with information about those fees in the DreamVault platform and, where supported
              and subject to your controls as applicable, DreamVault may automatically increase the prices of offerings
              to Builders to account for those fees.
            </p>
            <p>
              If a Builder pays in a currency that is different from the currency associated with their payment
              instrument, then the financial institution associated with that payment instrument may charge them a
              foreign transaction fee for that payment. We are not applying this fee and have no control over it, have
              no knowledge of whether the fee is applied, and do not receive any of the funds from that fee.
            </p>
            <p>
              Even if a Builder pays in the same currency as the currency associated with their payment instrument, when
              a Builder&apos;s payment is processed in a country different from the one associated with their payment
              instrument, then the financial institution associated with that payment instrument may charge them a
              foreign transaction fee for that payment. We are not applying this fee, have no knowledge of whether the
              fee is applied, and do not receive any of the funds from that fee, but our choices about payment
              processing may impact or otherwise give rise to this fee.
            </p>
            <p>
              <strong>Tax</strong>
            </p>
            <p>
              In order to satisfy our tax obligations, we collect tax identification information and, in certain
              circumstances, report this information and earnings to tax authorities as legally required. For example,
              if you are located in the United States or are a United States citizen who has earned $600 or more,
              DreamVault is required to issue you a Form 1099-K at year end. For purposes of Forms 1099-K, we treat all
              Dreamer earnings as earned for the sales of goods or services. You are responsible for reporting any
              income, withholding, or other earnings-based taxes which may be due as a result of money you&apos;ve
              earned on DreamVault. As a DreamVault user, you warrant (which means you make a legally enforceable
              promise) that you will at all times comply with all laws and regulations relating to taxes applicable to
              your use of DreamVault. If, at any point while you have a DreamVault account, any tax non-compliance
              occurs in relation to you (including a failure by you to report earnings or the imposition on you of any
              penalty or interest relating to tax) or if any litigation, inquiry or investigation is commenced against
              you that is in connection with, or that may lead to, any occurrence of tax non-compliance, you agree that
              you will notify us by email to{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>{" "}
              within seven days of the occurrence of the non-compliance or the commencement of the litigation, inquiry
              or investigation (as applicable). We are not responsible for advising you on your taxes and will not be
              liable for any non-payment of taxes by DreamVault users.
            </p>
            <p>
              We are additionally responsible for a number of transactional taxes across the world. These include, but
              are not limited to, Valued Added Tax (“VAT”), Goods and Services Tax (“GST”) and other local service or
              sales taxes in non-US locations (collectively referred to as “VAT”), as well as state sales tax in the
              United States. In locales where we are required to collect transaction tax from Builders, invoices
              presented to and payments processed from Builders will include the tier or custom payment amount plus the
              applicable tax. Applicable tax will be charged in addition to the price of a relevant transaction based
              upon the benefits that you indicate that you will be offering. Any applicable taxes are based on the rates
              applicable to the billing address you provide to us, and will be calculated at the time a transaction is
              charged. Unless otherwise indicated, all prices, fees and other charges are in U.S. dollars, and all
              payments shall be in U.S. currency.
            </p>
            <p>
              Amounts payable under this agreement between a Dreamer and DreamVault may include VAT or sales tax where
              applicable. You agree that you will provide us with any information and documents for accurate tax
              determination and compliance, where and as we request or as otherwise required.
            </p>
            <p>
              <strong>Restrictions</strong>
            </p>
            <p>
              We don&apos;t allow creations and benefits that violate our terms or policies. You can learn more by
              visiting our{" "}
              <Link className="text-primary underline" to="/legal/guidelines/community">
                Community Guidelines
              </Link>{" "}
              and{" "}
              <Link className="text-primary underline" to="/legal/guidelines/benefit">
                Benefit Guidelines
              </Link>
              . A high level summary of those rules is that we don&apos;t allow:
            </p>
            <ul>
              <li>illegal creations or benefits;</li>
              <li>creations or benefits that are abusive towards other people;</li>
              <li>creations or benefits that are misleading or deceptive;</li>
              <li>
                creations or benefits that use others&apos; intellectual property, unless you have written permission to
                use it, or your use is protected as fair use; or
              </li>
              <li>creations or benefits with Adult/18+ content, including all forms of nudity and sexual acts.</li>
            </ul>
            <p>
              If your Builders include people under the age of 18, then please remind them that they need their
              parent&apos;s or legal guardian&apos;s permission to make purchases on DreamVault, and that those under
              the age of 13 cannot use DreamVault. We are not required to allow any particular person or group of
              persons to access an offering, be a Builder or otherwise access DreamVault services.
            </p>
            <p>
              An account is tied to your creative output and cannot be sold or transferred for use by another Dreamer.
            </p>
            <p>
              We are constantly experimenting with product features to make DreamVault better and may add, remove, or
              test features with a subset of Dreamers. Access to these features is not guaranteed and may not be subject
              to your control. Instead, we may enable and disable these early-access, beta, or test features at our sole
              discretion. These features may also be subject to additional restrictions and limitations, including some
              that we describe through the DreamVault platform.
            </p>
            <p>
              <strong>Builders</strong>
            </p>
            <p>
              <em>
                To summarize: A Builder is someone who subscribes and/or makes a purchase on DreamVault, which may come
                with special benefits from Dreamers they love. Builders can purchase items for Dreamers or themselves on
                DreamVault through wish lists and Dreamers&apos; storefronts. Builders may receive benefits and directly
                interact with Dreamers using tools supplied by us, and, through DreamVault, have the opportunity to
                engage with a community of Builders and Dreamers.
              </em>
            </p>
            <p>
              As a Builder, you&apos;re supporting a Dreamer and may receive special benefits like shoutouts, exclusive
              creations, or merchandise. When you purchase gifts, DreamVault and the affiliate platform (e.g. Walmart)
              are each separate billing entities associated with the charge. You will see two charges for example if you
              purchase a gift; one charge for the item from the affiliate platform and one charge representing a
              percentage of that item&apos;s cost as the DreamVault platform fee. If your primary payment method fails
              and you&apos;ve configured one or more backup payment methods, then we will charge those backup payment
              methods based on that configuration.
            </p>
            <p>
              To become a Builder, simply purchase a gift or other offering on DreamVault. Subject to these terms and
              full payment of all applicable charges, to the extent an offering includes access to one or more of a
              Dreamer&apos;s creations, you receive a non-exclusive, non-transferable, non-sublicensable, revocable,
              limited license to access and view those creations for your own private, personal, non-promotional,
              non-commercial use. Creations you gain access to in this manner will generally continue to be available to
              you, but may become unavailable at any time as described below.
            </p>
            <p>
              If you are located in a jurisdiction in which DreamVault is required to charge and collect tax, for
              example VAT or sales tax, then this tax is added to the total charge. Except in limited circumstances,
              this tax is shown to you when purchasing an offering. DreamVault remits all tax collected to the
              applicable taxing authority. As tax is largely dependent on your location, you agree to keep your location
              complete and up to date in order to enable accurate collection of taxes.
            </p>
            <p>
              If you pay in a currency that is different from the currency associated with your payment instrument, then
              the financial institution associated with that payment instrument may charge you a foreign transaction fee
              for that payment. We are not applying this fee and have no control over it, have no knowledge of whether
              the fee is applied, and do not receive any of the funds from that fee.
            </p>
            <p>
              Even if you pay in the same currency as the currency associated with your payment instrument, when your
              payment is processed in a country different from the one associated with your payment instrument, then the
              financial institution associated with that payment instrument may charge you a foreign transaction fee for
              that payment. We are not applying this fee, have no knowledge of whether the fee is applied, and do not
              receive any of the funds from that fee, but our choices about payment processing may impact or otherwise
              give rise to this fee.
            </p>
            <p>
              We are constantly experimenting with product features to make DreamVault better and may add, remove, or
              test features with a subset of Builders. Access to these features is not guaranteed and may not be subject
              to your control. Instead, we may enable and disable these early-access, beta, or test features at our sole
              discretion. These features may also be subject to additional restrictions and limitations.
            </p>
            <p>
              All gifts are subject to the terms and conditions of the applicable vendor, which terms and conditions are
              stated on the applicable affiliate platform and on the applicable Dreamer&apos;s ordering list. Vendor,
              and not DreamVault, is solely responsible for the condition of gifts. All images of gifts are for
              illustrative purposes only, and gifts may vary from the pictures. Gift availability is subject to change
              without notice, and availability is not guaranteed. Although DreamVault uses its best efforts to ensure
              that the availability, colors and details of the gift are as described on the DreamVault platform,
              DreamVault does not guarantee that it will match in every instance.
            </p>
            <p>
              DreamVault does not control the delivery time or method of any gift, and DreamVault is not liable for any
              delivery or shipping issues or delays. DreamVault is not responsible for any gift lost in transit, stolen
              after delivery, or lost due to an incorrect, incomplete or outdated address, a PO box address or an
              otherwise non-residential address. DreamVault will not be obligated to offer refunds for any lost or
              stolen gifts and Builders should submit such request directly with the vendor affiliate platform. The
              delivery estimate given by DreamVault stems directly from the retailer&apos;s delivery estimate and
              DreamVault takes no responsibility if this delivery estimate is exceeded due to circumstances out of our
              control. Each Dreamer is solely responsible for updating wish lists for non-affiliate and unverified
              stores with current pricing and details.
            </p>
            <p>
              Once a Builder has purchased an item through the DreamVault platform, the order cannot be canceled or
              refunded unless the Dreamer chooses to cancel the order or refund the Builder themselves. DreamVault
              endeavors to keep ordering times as low as possible but please allow 30 business days for orders to be
              placed. If the order is not placed within 30 business days, Builder can request a refund. The Builder
              agrees to not dispute payments for orders that have not been placed within 30 business days of purchase.
              For the avoidance of doubt, the service provided by DreamVault to the Builder constitutes purchasing a
              given physical or digital good for the Dreamer, the Builder will not receive any tracking or order status
              information or physical or digital goods. DreamVault reserves the right to refuse any order in its sole
              discretion.
            </p>
            <p>
              <strong>Refunds.</strong> Our policy is not to provide refunds, including if you lose access to offerings
              as described above, though we may allow for some exceptions where refunds are granted at our sole
              discretion. If you made a purchase on affiliate platforms, then DreamVault may not be able to address any
              associated refund requests, and you may need to contact those platforms directly. When you request a
              refund for an item purchase through an affiliate platform, you do so directly with the affiliate platform.
              Even if the refund is granted from the affiliate platform, there is no refund for the DreamVault platform
              fee.
            </p>
            <p>
              <strong>Crowdfunding</strong>
            </p>
            <p>
              If applicable, each Dreamer may opt to allow more than one Builder to contribute to the pur- chase of a
              gift (the “<strong>Crowdfunded Gift</strong>”). In order to participate in the purchase of a Crowdfunded
              Gift, a Builder must contribute at least $5.00 to the purchase price (unless the remaining unpaid portion
              of the Crowdfunded Gift&apos;s purchase price is less than $5.00 and greater than $0.50). When the
              Crowdfunded Gift&apos;s total purchase price is not met by contributions from Builders, Dreamer may, as
              applicable, (i) wait until additional Builders contribute the remaining unpaid portion of the Crowdfunded
              Gift&apos;s purchase price, (ii) pay the remaining unpaid portion of the Crowdfunded Gift&apos;s purchase
              price through the payment processor or (iii) pay the remaining unpaid portion of the Crowdfunded
              Gift&apos;s purchase price from Dreamer&apos;s DreamVault balance.
            </p>
            <p>
              When undertaking a crowdfunded project, and especially when a project is successfully funded, Dreamers owe
              their Builders a high standard of effort, honest communication, and a dedication to bringing the project
              to life and using the crowdfunds for the purpose that was originally set out. At the same time, Builders
              agree and acknowledge they&apos;re not buying something when they back a project—they&apos;re helping to
              create something new, not ordering something that already exists. Every project is different. There may be
              changes or delays, and there&apos;s a chance something could happen that prevents the Dreamer from being
              able to finish the project, or not use the crowdfunds for the original stated purpose, which is not
              guaranteed. Dreamers are required to be honest, make no material misrepresentations in their communication
              to Builders, and make every reasonable effort to use the crowdfunds for the originally stated purpose.
              DreamVault is not liable for any damages or losses related to Builders and Dreamers use of DreamVault and
              the crowdfunding service.
            </p>
            <p>
              <strong>Cryptocurrency Payment Terms</strong>
            </p>
            <p>
              Dreamers and Builders may have the option to make/receive payments using cryptocurrency. By choosing to
              pay with cryptocurrency, you agree to the following terms in addition to our payment terms above. You also
              agree to our cryptocurrency payment partner Sphere and the terms found here{" "}
              <a target="_blank" href="https://spherepay.co/legal/terms">
                <span className="text-primary">https://spherepay.co/legal/terms</span>
              </a>
              .
            </p>
            <p>
              Cryptocurrency prices are highly volatile and subject to fluctuations. The exchange rate at the time of
              payment will apply. We are not responsible for any changes in value between the time of payment and the
              completion of the transaction.
            </p>
            <p>
              Transactions must be fully confirmed on the respective blockchain before services are rendered. We reserve
              the right to delay the provision of services until we have received full confirmation of the payment.
            </p>
            <p>There are no refunds for payments made in cryptocurrency. </p>
            <p>
              You are responsible for ensuring the security of your cryptocurrency wallet and for any errors or losses
              related to the transfer of cryptocurrency. We do not assume liability for lost, stolen, or misdirected
              cryptocurrency payments.
            </p>
            <p>
              By using cryptocurrency as a payment method, you acknowledge that it is your responsibility to ensure
              compliance with any local laws and regulations regarding the use of cryptocurrencies. We reserve the right
              to refuse or cancel transactions that we believe are in violation of applicable laws or our policies. You
              may not use cryptocurrency payment services, as it pertains to sanctions or other similar international
              regulations, you are in, under the control of, or a national or resident of Cuba, Iran, North Korea,
              Sudan, Syria, or any other country subject to United States embargo, UN Security Council Resolutions
              (“UNSCR”), HM Treasury&apos;s financial sanctions regime, or if you are on the U.S. Treasury
              Department&apos;s Specially Designated Nationals List or the U.S. Commerce Department&apos;s Denied
              Persons List, Unverified List, Entity List HM Treasury&apos;s financial sanctions regime. Similarly, you
              may not use Sphere Services or Sphere-Enabled Services if you intend to supply any Services to Cuba, Iran,
              North Korea, Sudan, Syria, or any other country subject to United States embargo or HM Treasury&apos;s
              financial sanctions regime, or to a person on the Specially Designated Nationals List, Denied Persons
              List, Unverified List, Entity List, or HM Treasury&apos;s financial sanctions regime. You agree to comply
              with all applicable export control and trade sanctions laws.
            </p>
            <p>
              BY USING CRYPTOCURRENCY AS A PAYMENT METHOD, YOU EXPRESSLY ACKNOWLEDGE AND ACCEPT YOUR SOLE RESPONSIBILITY
              FOR COMPLIANCE WITH ALL APPLICABLE ANTI-MONEY LAUNDERING (AML) AND COUNTER-TERRORISM FINANCING (CFT)
              REGULATIONS IN CONNECTION WITH YOUR USAGE OF CRYPTOCURRENCY PAYMENT SERVICES. YOU AGREE TO UTILIZE
              CRYPTOCURRENCY PAYMENT SERVICES IN A LEGAL MANNER, STRICTLY IN ACCORDANCE WITH ALL RELEVANT LAWS AND
              REGULATIONS.
            </p>
            <p>
              We reserve the right to modify these terms related to cryptocurrency payments at any time. Any changes
              will be effective immediately upon posting on our website. It is your responsibility to review these terms
              regularly.
            </p>
            <p>
              <strong>DreamVault&apos;s role</strong>
            </p>
            <p>
              <em>
                To summarize: We may look at some pages and posts on DreamVault and review reported pages to identify
                potential violations of these terms and our policies.
              </em>
            </p>
            <p>
              We look at some pages and posts on DreamVault to enforce these terms and our policies, including our{" "}
              <Link className="text-primary underline" to="/legal/guidelines/community">
                Community Guidelines
              </Link>{" "}
              and{" "}
              <Link className="text-primary underline" to="/legal/guidelines/benefit">
                Benefit Guidelines
              </Link>
              We also investigate reports of potential violations. These investigations may take a while to resolve and,
              primarily because DreamVault may be exposed to risk based on what is done with funds earned on DreamVault,
              may include reviewing activity outside of DreamVault.
            </p>
            <p>
              In most situations, we will work with Dreamers and Builders to resolve any potential policy violations and
              allow the Dreamer or Builder to continue using DreamVault. DreamVault may terminate Dreamer or Builder
              accounts when we believe it is necessary to protect Dreamers, Builders, DreamVault, or the broader
              community, but it is not an action we take lightly.
            </p>
            <p>
              Please let us know if you see potential violations of our{" "}
              <Link className="text-primary underline" to="/legal/guidelines/community">
                Community Guidelines
              </Link>
              . You can report violations by emailing us at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              .
            </p>
            <p>
              As a global company based in the United States with operations in other countries, we must comply with
              economic sanctions and trade restrictions, including those implemented by the Office of Foreign Assets
              Control (“OFAC”) of the United States Department of the Treasury. This means that we cannot take part in
              transactions that involve designated people, places, or items that originate from those places, as
              determined by agencies like OFAC.
            </p>
            <p>
              <strong>Security</strong>
            </p>
            <p>
              The security of your personal information is important to us. We use reasonable and appropriate physical,
              technical, and administrative industry safeguards to protect information from unauthorized access, use,
              loss, misuse or unauthorized alteration.
            </p>
            <p>
              Our systems never access your full credit card number, and we encrypt the most sensitive information like
              tax information using transport layer security (TLS). We also offer advanced security measures, such as
              two-factor authentication for signing in, and CAPTCHA verification in cases when a bot is detected, when a
              user attempts to login from a suspicious IP address or after rapid succession login attempts. We only
              serve traffic over HTTPS, which encrypts all data between you and us.
            </p>
            <p>
              <span className="underline">
                <em>What you should do</em>
              </span>
              . As a Dreamer or Builder, you can help prevent unauthorized access to your account and information by
              choosing a strong and unique password which is stored in a password manager. If you have any questions
              about the security of your personal information, you can contact us at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              .
            </p>
            <p>
              <span className="underline">
                <em>What you should not do</em>
              </span>
              <em>.</em>Please do not engage in any behavior that endangers the security of our website and users. Below
              are some examples of things you should not do on DreamVault.
            </p>
            <ul>
              <li>Illegal Activities - Don&apos;t break the law or encourage others to break the law.</li>
              <li>
                Personal Information - Don&apos;t distribute others&apos; personal information or otherwise abuse it.
                Dreamers with access to their Builders&apos; personal information should not use it for anything
                unrelated to DreamVault.
              </li>
              <li>Spam - Don&apos;t spam others or distribute unsolicited advertising material.</li>
              <li>Malware - Don&apos;t use DreamVault to host or distribute, malicious or destructive software.</li>
              <li>
                Service Degradation - Don&apos;t degrade others&apos; use of DreamVault or do any kind of brute force or
                load testing.
              </li>
              <li>
                Data Mining - Don&apos;t crawl, scrape or otherwise index information on DreamVault. If you are doing
                this to create a useful feature then we may allow it, but you must check with us first.
              </li>
              <li>
                Reverse Engineering - Don&apos;t reverse engineer or access our code in any manner that is not
                authorized by us.
              </li>
            </ul>
            <p>
              If you engage in any of these behaviors, or otherwise endanger the security of our website and users we
              may terminate your account.
            </p>
            <p>
              <strong>Account deletion</strong>
            </p>
            <p>
              <em>To summarize: You can delete your account by emailing us at</em>{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">
                  <em>{CONTACT_EMAIL}</em>
                </span>
              </a>
              . <em>We can terminate or disable your account at our sole discretion.</em>
            </p>
            <p>
              You can permanently delete your account at any time by emailing us at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              .
            </p>
            <p>
              We can terminate or suspend your account at any time at our sole discretion. We can also cancel any
              membership subscriptions and remove any descriptions, posts, offerings, or benefits at our sole
              discretion. You may not bring a claim against us for suspending or terminating another person&apos;s
              account, and you agree you will not bring such a claim. If you try to bring such a claim, you are
              responsible for the damages caused, including attorneys&apos; fees and costs. These terms remain in effect
              even if you no longer have an account.
            </p>
            <p>
              <strong>Your creations</strong>
            </p>
            <p>
              <em>
                To summarize: Dreamers keep full ownership of their creations and Builders keep full ownership of what
                they post on DreamVault, but give us permission to use them as part of DreamVault&apos;s services. Make
                sure you have permission to post the creations that you make available on DreamVault.
              </em>
            </p>
            <p>
              Dreamers keep full ownership of their creations and Builders keep full ownership of what they post on
              DreamVault, but we need licenses from you for this material in order to operate DreamVault&apos;s services
              effectively. By making creations available on DreamVault or otherwise posting on DreamVault, you grant us
              a royalty-free, perpetual, irrevocable, non-exclusive, sublicensable, worldwide license covering your
              creation or what you post in all formats and channels now known or later developed anywhere in the world
              to use, copy, reproduce, store, translate, transmit, distribute, perform, prepare derivative works,
              publicly display, and display in connection with any name, username, voice, or likeness provided in
              connection with it. If your creations contain any personal data, you also recognize DreamVault&apos;s
              “legitimate interest” in it in accordance with the scope of this license.
            </p>
            <p>
              This license is important for us to operate our services, including hosting creations, providing and
              promoting memberships to your Builders, and enabling community features like post comments. For clarity,
              you keep full ownership over your creations and what you post on DreamVault; we are not buying your
              intellectual property rights or leasing them from you for our gain. We will never try to steal your
              creations, use them in an exploitative way, or seek to profit off of them by any means other than
              facilitating memberships and offerings you&apos;ve configured on DreamVault.
            </p>
            <p>
              You agree not to make available creations or otherwise make posts on DreamVault that infringe others&apos;
              intellectual property or proprietary rights. DreamVault reserves the right to remove creations and other
              material that violate DreamVault&apos;s terms and policies, including these Terms of Use and the{" "}
              <Link className="text-primary underline" to="/legal/guidelines/community">
                Community Guidelines
              </Link>
              . We respond to compliant notifications of claimed copyright infringement and have adopted and reasonably
              implemented a policy for terminating, in appropriate circumstances, the accounts of Dreamers and Builders
              who are the subject of repeated compliant notifications of claimed copyright infringement or other
              evidence that suggests a pattern of repeat infringement. Information regarding how this policy is
              implemented as well as the process for reporting or responding to notifications of claimed copyright
              infringement can be found{" "}
              <Link className="text-primary underline" to="/legal/policy/copyright-trademark">
                here
              </Link>
              .
            </p>
            <p>
              Builders may not use creations posted by Dreamers in any way not authorized by the Dreamer, including
              sharing those creations with others who have not purchased the offering or subscription through which
              those creations were made available.
            </p>
            <p>
              <strong>Third-party apps and services</strong>
            </p>
            <p>
              <em>
                To summarize: You may grant third-party apps and services access to your DreamVault account and you may
                grant DreamVault access to third-party apps and services. You may also revoke this access.
              </em>
            </p>
            <p>
              When you connect your DreamVault account to other websites, apps, or services, you may be asked to give
              those websites, apps, or services access to information about your DreamVault account and/or permission to
              perform actions within your DreamVault account on your behalf. If you choose to take this authorization
              action, then we will follow your instructions and grant the access or permission that you&apos;ve
              selected. You can learn more in our{" "}
              <Link className="text-primary underline" to="/legal/privacy">
                Privacy Policy
              </Link>
              .
            </p>
            <p>
              <strong>DreamVault&apos;s creations</strong>
            </p>
            <p>
              <em>
                To summarize: You agree that any bug reports or suggested improvements that you provide to DreamVault
                will be fully owned by DreamVault. Dreamers can use our copyrights or trademarks to promote their
                DreamVault pages, but can&apos;t use them for anything else without our permission.
              </em>
            </p>
            <p>
              Our creations are protected by copyright, trademark, patent, and trade secret laws. Some examples of our
              creations are the text on the{" "}
              <a target="_blank" href="http://www.dreamvault.co">
                <span className="text-primary">www.dreamvault.co</span>
              </a>{" "}
              site, the text on DreamVault&apos;s other websites, our iOS and Android apps, our APIs, our logo, and our
              codebase. We grant Dreamers a license to use our logo and other copyrights or trademarks to promote their
              DreamVault pages. You can learn more about the correct use of our logo in our Brand Guidelines. You may
              not otherwise use, reproduce, distribute, perform, publicly display, or prepare derivative works of our
              creations unless we give you permission in writing.
            </p>
            <p>
              We welcome your feedback on the performance of the DreamVault product, including any early-access, beta,
              or test features to which you&apos;ve been given access, and your help in identifying any problems, bugs,
              and defects. You agree that any suggestions, comments, ideas, bug reports, or suggested improvements that
              you provide to us will be fully owned by us so that we can use them to improve DreamVault and thereby
              better help the community of Dreamers and Builders.
            </p>
            <p>
              <strong>Indemnity</strong>
            </p>
            <p>
              <em>
                To summarize: If we are sued because of your use of or conduct on DreamVault, you have to help pay for
                it.
              </em>
            </p>
            <p>
              You will indemnify us from all losses and liabilities, including legal fees, that arise from these terms
              or relate to your use of DreamVault. We reserve the right to exclusive control over the defense of a claim
              covered by this clause. If we use this right, then you will help us in our defense. Your obligation to
              indemnify under this clause also applies to our subsidiaries, affiliates, officers, directors, employees,
              agents, and third-party service providers.
            </p>
            <p>
              <strong>Warranty disclaimer</strong>
            </p>
            <p>
              <em>To summarize: We do our best to make sure DreamVault works as expected, but things happen.</em>
            </p>
            <p>
              <strong>
                YOUR USE OF DREAMVAULT IS AT YOUR SOLE RISK. DREAMVAULT IS PROVIDED ON AN “AS IS” AND “AS AVAILABLE”
                BASIS. EXCEPT AS OTHERWISE EXPRESSLY PROVIDED HEREIN, WE EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND,
                WHETHER EXPRESS, IMPLIED OR STATUTORY, INCLUDING, BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT. THE DISCLAIMERS OF
                WARRANTY UNDER THIS CLAUSE ALSO APPLY TO OUR SUBSIDIARIES, AFFILIATES, AND THIRD-PARTY SERVICE
                PROVIDERS.
              </strong>
            </p>
            <p>
              <strong>
                WE MAKE NO WARRANTY THAT (I) DREAMVAULT WILL MEET YOUR REQUIREMENTS, (II) DREAMVAULT WILL BE
                UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE, (III) THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF
                DREAMVAULT WILL BE ACCURATE OR RELIABLE, OR (IV) THE QUALITY OF ANY PRODUCTS, SERVICES, INFORMATION, OR
                OTHER MATERIAL PURCHASED OR OBTAINED BY YOU THROUGH DREAMVAULT WILL MEET YOUR EXPECTATIONS.
              </strong>
            </p>
            <p>
              <strong>Limitation of liability</strong>
            </p>
            <p>
              <em>
                To summarize: If you lose money as a result of using DreamVault, any payment to you is limited to the
                amount of money we have earned through your use of DreamVault.
              </em>
            </p>
            <p>
              <strong>
                YOU EXPRESSLY UNDERSTAND AND AGREE THAT WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, EXEMPLARY DAMAGES, OR DAMAGES FOR LOSS OF PROFITS INCLUDING BUT NOT LIMITED TO, DAMAGES
                FOR LOSS OF GOODWILL, USE, DATA OR OTHER INTANGIBLE LOSSES (EVEN IF WE HAVE BEEN ADVISED OF THE
                POSSIBILITY OF SUCH DAMAGES), WHETHER BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY OR
                OTHERWISE, RESULTING FROM: (I) THE USE OR THE INABILITY TO USE DREAMVAULT; (II) THE COST OF PROCUREMENT
                OF SUBSTITUTE GOODS AND SERVICES RESULTING FROM ANY GOODS, DATA, INFORMATION OR SERVICES PURCHASED OR
                OBTAINED OR MESSAGES RECEIVED OR TRANSACTIONS ENTERED INTO THROUGH OR FROM DREAMVAULT; (III)
                UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS OR DATA; (IV) STATEMENTS OR CONDUCT OF ANY
                THIRD PARTY ON DREAMVAULT; OR (V) ANY OTHER MATTER RELATING TO DREAMVAULT. IN NO EVENT WILL OUR TOTAL
                LIABILITY TO YOU FOR ALL DAMAGES, LOSSES OR CAUSES OF ACTION EXCEED THE AMOUNT YOU HAVE PAID US IN THE
                LAST SIX (6) MONTHS, OR, IF GREATER, ONE HUNDRED DOLLARS ($100). FOR THIS CLAUSE, “WE” AND “OUR” IS
                DEFINED TO INCLUDE OUR SUBSIDIARIES, AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND THIRD-PARTY
                SERVICE PROVIDERS.
              </strong>
            </p>
            <p>
              <strong>
                SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR THE LIMITATION OR EXCLUSION OF
                LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, SOME OF THE ABOVE LIMITATIONS SET FORTH
                ABOVE MAY NOT APPLY TO YOU. IF YOU ARE DISSATISFIED WITH ANY PORTION OF THE SERVICE OR WITH THESE TERMS,
                YOUR SOLE AND EXCLUSIVE REMEDY IS TO DISCONTINUE USE OF DREAMVAULT.
              </strong>
            </p>
            <p>
              <strong>Dispute resolution; Binding Arbitration; Class Action Waiver</strong>
            </p>
            <p>
              <em>
                To summarize: If you have a problem, please talk to us. Any disputes with us must be resolved in Los
                Angeles through arbitration under California law.
              </em>
            </p>
            <p>
              You and us agree that these Terms of Use affect interstate commerce and that the U.S. Federal Arbitration
              Act governs the interpretation and enforcement of these arbitration provisions. This Section is intended
              to be interpreted broadly and governs any and all disputes between us including but not limited to claims
              arising out of or relating to any aspect of the relationship between us or the Terms of Use or DreamVault,
              whether based in contract, tort, statute, fraud, misrepresentation or any other legal theory. The only
              disputes excluded from this broad prohibition are the litigation of certain intellectual property and
              small court claims, as provided below.
            </p>
            <p>
              If you have any dispute with us, you agree that before taking any formal action, you will contact us at
              DreamVault LLC, 11509 Beatrice St., Los Angeles, CA, 90066 and{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>{" "}
              , and provide a brief, written description of the dispute and your contact information (including your
              username, if your dispute relates to an account). Except for intellectual property and small claims court
              claims, the parties agree to use their best efforts to settle any dispute, claim, question, or
              disagreement directly through consultation and good faith negotiations shall be a condition to either
              party initiating a lawsuit or arbitration.
            </p>
            <p>
              All disputes, claims, or controversies arising out of or relating to the Terms of Use or DreamVault that
              are not resolved by the procedures identified above shall be resolved by individual (not group) binding
              arbitration to be conducted before JAMS in accordance with the JAMS Streamlined Arbitration Procedure
              Rules for claims that do not exceed $250,000 and the JAMS Comprehensive Arbitration Rules and Procedures
              for claims exceeding $250,000 in effect at the time the arbitration is initiated, excluding any rules or
              procedures governing or permitting class actions. Arbitration will be held in Los Angeles, California, and
              you and us agree to submit to the personal jurisdiction of any federal or state court in Los Angeles,
              California in order to compel arbitration, stay proceedings pending arbitration, or to confirm, modify,
              vacate, or enter judgment on the award entered by the arbitrator.
            </p>
            <p>
              The arbitrator, and not any federal, state, or local court or agency, shall have exclusive authority to
              resolve all disputes arising out of or relating to the interpretation, applicability, enforceability, or
              formation of these Terms of Use, including but not limited to any claim that all or any part of these
              Terms of Use are void or voidable, or whether a claim is subject to arbitration. The arbitrator shall be
              empowered to grant whatever relief would be available in a court under law or in equity. The
              arbitrator&apos;s award shall be written and shall be binding on the parties and may be entered as a
              judgment in any court of competent jurisdiction.
            </p>
            <p>
              We shall bear the cost of any arbitration filing fees and arbitration fees for claims of up to $75,000,
              unless the arbitrator finds the arbitration to be frivolous. You are responsible for all other additional
              costs that you may incur in the arbitration including but not limited to attorneys&apos; fees and expert
              witness costs unless we are otherwise specifically required to pay such fees under applicable law. If your
              claim is solely for monetary relief of $10,000 or less, and does not include a request for any type of
              equitable remedy, you may choose whether the arbitration will be conducted solely based on documents
              submitted to the arbitrator, through a telephonic hearing, or by an in-person hearing.
            </p>
            <p>
              You and us agree that the arbitration shall be conducted in the party&apos;s respective individual
              capacities only and not as a class action or other representative action, and the parties expressly waive
              their right to file a class action or seek relief on a class basis. YOU AND US AGREE THAT EACH MAY BRING
              CLAIMS AGAINST THE OTHER ONLY IN YOUR OR OUR INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER
              IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING. If any court or arbitrator determines that the class
              action waiver set forth in this paragraph is void or unenforceable for any reason or that an arbitration
              can proceed on a class basis, then the arbitration provisions set forth above shall be deemed null and
              void in their entirety and the parties shall be deemed to have not agreed to arbitrate disputes.
            </p>
            <p>
              Notwithstanding the parties&apos; decision to resolve all disputes through arbitration, you or we may
              bring enforcement actions, validity determinations or claims arising from or relating to theft, piracy or
              unauthorized use of intellectual property in state or federal court with jurisdiction or in the U.S.
              Patent and Trademark Office to protect intellectual property rights (&quot;intellectual property
              rights&quot; means patents, copyrights, moral rights, trademarks, and trade secrets, but not privacy or
              publicity rights). You or we may also seek relief in a small claims court for disputes or claims within
              the scope of that court&apos;s jurisdiction to the extent such claims do not seek equitable relief.
            </p>
            <p>
              You have the right to opt out and not be bound by the arbitration and class action waiver provisions set
              forth above by sending written notice of your decision to opt out to us at the address identified in at
              the bottom of these terms. The notice must be sent within thirty (30) days of (a) the “Last Updated” date
              of these Terms of Use as set forth above; or (b) your first date that you used DreamVault that contained
              any versions of the Terms of Use that included this version of the mandatory arbitration and class action
              waiver, whichever is later. Otherwise you shall be bound to arbitrate disputes in accordance with the
              terms of these paragraphs. If you opt out of these arbitration provisions, we also will not be bound by
              them. If we change this &apos;Arbitration&apos; section after the date you first accepted these Terms of
              Use (or accepted any subsequent changes to these Terms of Use), you may reject any such change by sending
              us written notice within 30 days of the date such change became effective, as indicated in the “Last
              Updated” date above or in the date of our email to you notifying you of such change. By rejecting any
              change, you are agreeing that you will arbitrate any dispute between you and us in accordance with the
              provisions of this Section as of the date you first accepted these Terms of Use (or accepted any
              subsequent changes to these Terms of Use).
            </p>
            <p>
              YOU UNDERSTAND AND AGREE THAT THE ABOVE DISPUTE PROCEDURES SHALL BE YOUR SOLE REMEDY IN THE EVENT OF
              DISPUTE BETWEEN YOU AND US REGARDING ANY ASPECT OF DREAMVAULT (INCLUDING THE ENROLLMENT PROCESS) AND THAT
              YOU ARE WAIVING YOUR RIGHT TO LEAD OR PARTICIPATE IN A LAWSUIT INVOLVING OTHER PERSONS, SUCH AS A CLASS
              ACTION.
            </p>
            <p>
              <strong>Everything else</strong>
            </p>
            <p>
              <em>
                To summarize: These terms are the final word on DreamVault&apos;s policies and we will tell you if we
                make material changes to them.
              </em>
            </p>
            <p>
              These terms and any referenced policies are the entire agreement between you and us, supersede all prior
              agreements, and don&apos;t create any partnership, joint venture, employee-employer or
              franchiser-franchisee relationship between you and us. If any provision of these terms is held to be
              unenforceable, then that provision is modified to the extent necessary to enforce it. If a provision
              cannot be modified to make it enforceable, then it is severed from these terms, and all other provisions
              remain in force. If either party fails to enforce a right provided by these terms, then it does not waive
              the ability to enforce any rights in the future.
            </p>
            <p>
              We may sometimes make changes to these terms. If we make a change to these terms that, in our sole
              discretion, is material, then we will let you know before the changes come into effect. Continuing to use
              DreamVault after a change means you accept the new terms or policies.
            </p>
            <p>
              If you sign-up to receive text messages from us, you agree to receive recurring automated promotional and
              personalized marketing text (e.g., SMS and MMS) messages from us, including text messages that may be sent
              using an automatic telephone dialing system, to the mobile telephone number you provided when signing up
              or any other number that you designate. Consent to receive automated marketing text messages is not a
              condition of any purchase. Message and data rates may apply. Message frequency may vary. We reserve the
              right to alter the frequency of messages sent at any time, so as to increase or decrease the total number
              of sent messages. We also reserve the right to change the short code or phone number from which messages
              are sent. Not all mobile devices or handsets may be supported and our messages may not be deliverable in
              all areas. We, our service providers, and the mobile carriers supported by the program are not liable for
              delayed or undelivered messages.
            </p>
            <p>
              <strong>Contact</strong>
            </p>
            <p>DreamVault LLC</p>
            <p>11509 Beatrice St.</p>
            <p>Los Angeles, CA, 90066</p>
            <p>
              {" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>{" "}
            </p>
          </article>
        </div>
      </SingleColumn.Main>
    </SingleColumn.Root>
  );
}
